/**
 * International Auctions — the index page (F1.1, BXS-3627).
 *
 * Route: `/:locale/international-auctions`. Renders the mockup's `isIndex`
 * state: dark hero band, a three-group pill filter bar, the live/results
 * counter, the responsive card grid, and the preview-only disclaimer.
 *
 * WHY THE FILTERS LIVE IN THE URL
 * -------------------------------
 * House / status / sale type are query params (`?house=&status=&type=`),
 * exactly as `buyer-auctions/pages/auctions-list` does it. A pill click calls
 * `router.navigate` with merged query params and the page reacts to
 * `route.queryParams` — it never sets its own filter fields directly. That is
 * what makes a filtered view shareable and the browser back button meaningful;
 * component fields give neither. `null` in the merged params removes the key,
 * so "All" leaves a clean URL.
 *
 * The mock service answers on a ~250 ms delay, so a filter change genuinely
 * races: `switchMap` cancels the in-flight read, which is why a fast run
 * through the pills can never paint a stale result set.
 *
 * SORTING IS NOT DONE HERE. `listSales()` returns live → upcoming → preview →
 * closed already (see `compareSalesForDisplay`); re-sorting would be a second
 * implementation of the same rule waiting to drift.
 *
 * SSR: no `window` / `document` / `localStorage` anywhere in this file.
 *
 * Mockup: docs/external-cataloge/Bidxsell co-branded catalogue/.../International Auctions.dc.html
 */
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { LocaleLinkPipe } from 'src/app/Shared/_pipes/locale-link/locale-link.pipe';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';

import { InternationalAuctionsService } from '../../../_services/international-auctions.service';
import {
  ExternalHouse,
  ExternalSaleStatus,
  ExternalSaleType,
  ExternalSaleView,
  SupportedLocale,
  resolveTranslation,
} from '../_mock/international-auctions.models';
import { InternationalSaleCardComponent } from '../_shared/sale-card/international-sale-card.component';

/** The four statuses, in the order the mockup lists them. */
const SALE_STATUSES: readonly ExternalSaleStatus[] = [
  'live',
  'upcoming',
  'preview',
  'closed',
];

/** How many card-shaped placeholders the loading grid paints. */
const SKELETON_CARDS: readonly number[] = [0, 1, 2, 3, 4, 5];

@Component({
  selector: 'app-international-auctions-index',
  standalone: true,
  imports: [
    RouterLink,
    TranslatePipe,
    LocaleLinkPipe,
    InternationalSaleCardComponent,
  ],
  templateUrl: './international-auctions-index.component.html',
  styleUrl: './international-auctions-index.component.scss',
})
export class InternationalAuctionsIndexComponent implements OnInit, OnDestroy {
  private readonly auctions = inject(InternationalAuctionsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localeService = inject(LocaleService);

  /** Filter pill options. Both lists come from the service, never hard-coded. */
  houses: ExternalHouse[] = [];
  saleTypes: ExternalSaleType[] = [];
  readonly statuses = SALE_STATUSES;

  /** Already sorted by the service — do not re-sort. */
  sales: ExternalSaleView[] = [];

  /**
   * Live sales across the WHOLE catalogue, not the filtered view: the counter
   * reads "N auctions live now" regardless of which filters are on, exactly as
   * the mockup computes it.
   */
  liveCount = 0;

  /** True from the moment a query is issued until its results land. */
  isLoading = true;

  /** Mirrors of the query params, so a pill can render its active state. */
  house: string | null = null;
  status: ExternalSaleStatus | null = null;
  saleType: string | null = null;

  /** Drives `resolveTranslation` for the sale-type pill labels. */
  locale: SupportedLocale = 'en';

  readonly skeletonCards = SKELETON_CARDS;

  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    // Subscribed rather than read once: switching language must relabel the
    // sale-type pills without a reload.
    this.subscriptions.add(
      this.localeService.languageCode$.subscribe((code) => {
        this.locale = code === 'ar' ? 'ar' : 'en';
      })
    );

    this.subscriptions.add(
      this.auctions
        .listHouses()
        .pipe(catchError(() => of([] as ExternalHouse[])))
        .subscribe((houses) => {
          this.houses = houses;
        })
    );

    this.subscriptions.add(
      this.auctions
        .listSaleTypes()
        .pipe(catchError(() => of([] as ExternalSaleType[])))
        .subscribe((types) => {
          this.saleTypes = types;
        })
    );

    // Unfiltered, and fetched once — the live count is a property of the
    // catalogue, not of the current filter selection.
    this.subscriptions.add(
      this.auctions
        .getSaleCounts()
        .pipe(catchError(() => of(null)))
        .subscribe((counts) => {
          this.liveCount = counts?.live ?? 0;
        })
    );

    // The URL is the single source of truth for the filter state.
    this.subscriptions.add(
      this.route.queryParams
        .pipe(
          switchMap((params: Params) => {
            this.house = this.readText(params['house']);
            this.status = this.readStatus(params['status']);
            this.saleType = this.readText(params['type']);
            this.isLoading = true;

            return this.auctions
              .listSales({
                house: this.house,
                status: this.status,
                sale_type: this.saleType,
              })
              .pipe(catchError(() => of([] as ExternalSaleView[])));
          })
        )
        .subscribe((sales) => {
          this.sales = sales;
          this.isLoading = false;
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /* --- Labels ------------------------------------------------------------ */

  /**
   * House names are proper nouns and are NOT translated (see `ExternalHouse`),
   * so the pill shows the name verbatim.
   */
  houseLabel(house: ExternalHouse): string {
    return house.name;
  }

  /** Sale-type names ARE translated, with the platform's en → any fallback. */
  saleTypeLabel(type: ExternalSaleType): string {
    return resolveTranslation(type.name, this.locale) ?? type.slug;
  }

  statusKey(status: ExternalSaleStatus): string {
    return `international_auctions.status.${status}`;
  }

  /* --- Counters ----------------------------------------------------------- */

  /** ngx-translate has no ICU plurals, so the key is chosen here. */
  get liveCounterKey(): string {
    if (this.liveCount === 0) {
      return 'international_auctions.counter.live_none';
    }
    return this.liveCount === 1
      ? 'international_auctions.counter.live_one'
      : 'international_auctions.counter.live_other';
  }

  get resultsCounterKey(): string {
    return this.sales.length === 1
      ? 'international_auctions.counter.results_one'
      : 'international_auctions.counter.results_other';
  }

  get hasActiveFilters(): boolean {
    return !!this.house || !!this.status || !!this.saleType;
  }

  /* --- Filter actions ----------------------------------------------------- */

  selectHouse(slug: string | null): void {
    this.applyFilters({ house: slug });
  }

  selectStatus(status: ExternalSaleStatus | null): void {
    this.applyFilters({ status });
  }

  selectSaleType(slug: string | null): void {
    this.applyFilters({ type: slug });
  }

  resetFilters(): void {
    this.applyFilters({ house: null, status: null, type: null });
  }

  /**
   * Merged, so an unrelated query param on the URL survives a filter change.
   * A `null` value removes its key rather than writing `?house=`, which keeps
   * a shared link clean and keeps "All" indistinguishable from "never set".
   */
  private applyFilters(
    patch: Partial<Record<'house' | 'status' | 'type', string | null>>
  ): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: patch,
      queryParamsHandling: 'merge',
    });
  }

  /* --- Query-param parsing ------------------------------------------------ */

  private readText(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  /**
   * An unknown `?status=` is dropped rather than passed through, so a mangled
   * link renders the full list instead of a permanently empty one.
   */
  private readStatus(value: unknown): ExternalSaleStatus | null {
    const text = this.readText(value)?.toLowerCase() ?? null;
    if (!text) {
      return null;
    }
    return SALE_STATUSES.includes(text as ExternalSaleStatus)
      ? (text as ExternalSaleStatus)
      : null;
  }
}
